import { useParams } from "react-router-dom";
import { EuiFormRow, EuiPanel, EuiButton, EuiSpacer } from "@elastic/eui";
import { PurchaseLegalSignaturePdf } from "../../components/plans/PurchaseLegalSignaturePdf";
import { ReactSketchCanvas } from "react-sketch-canvas";
import { StripeAddPaymentMethod } from "../../components/payments/StripeAddPaymentMethod";
import React, { useEffect, useState } from "react";
import {
  useGetPlanAgreementWithESignTokenQuery,
  useSignAndActivatePlanMutation,
} from "../../api/rtkQueryApi/AgreementsApi";
import {
  CreateAddonLedgerFromAddonDetails,
  INewOrderDetails,
} from "../newOrder/useNewOrderDetails";
import { Document, Page, usePDF } from "@react-pdf/renderer";
import { AddonBillingType } from "../../api/entities/Addon";
import { useDebounceEffect } from "../../utils/useDebounceEffect";

export const RemotePurchase = () => {
  const { eSignToken } = useParams();
  const getPlanAgreementWithESignTokenQuery =
    useGetPlanAgreementWithESignTokenQuery(
      { eSignToken: eSignToken ?? "" },
      { skip: !eSignToken },
    );
  const [signAndActivatePlan, signAndActivatePlanQuery] =
    useSignAndActivatePlanMutation();
  const [confirmClicked, setConfirmClicked] = useState(false);

  const [hasDrawn, setHasDrawn] = useState(false);
  const signatureRef = React.useRef<any>();
  const [signatureUrl, setSignatureUrl] = React.useState<string>();
  const [pdfKey, setPdfKey] = React.useState(0);

  const [orderDetails, setOrderDetails] = useState<INewOrderDetails>();
  const [isLoading, setIsLoading] = useState(false);
  const [showPurchaseComplete, setShowPurchaseComplete] = useState(false);
  const [finalizeReady, setFinalizeReady] = useState(false);

  const [instance, updateInstance] = usePDF({
    document: (
      <Document>
        <Page size="A4" />
      </Document>
    ),
  });

  const business = getPlanAgreementWithESignTokenQuery.data?.business;

  useEffect(() => {
    if (getPlanAgreementWithESignTokenQuery.data) {
      const plan = getPlanAgreementWithESignTokenQuery.data.plan;
      setOrderDetails({
        productId: "",
        addons: plan.addonDetailsList,
        totalMonthlyPrice:
          plan.productDetails.pricePerMonth +
          plan.addonDetailsList.reduce(
            (acc, addon) =>
              acc + addon.addonBillingType === AddonBillingType.ParentRecurring
                ? addon.price
                : 0,
            0,
          ),
        discount: plan.discount,
        isOverrideSetupFee: plan.isOverrideSetupFee,
        overrideSetupFee: plan.overrideSetupFee,
        addonLedger: CreateAddonLedgerFromAddonDetails(
          plan.addonDetailsList,
          plan.productDetails,
        ),
        productDetails: plan.productDetails,
        productGroup: getPlanAgreementWithESignTokenQuery.data.productGroup,
        totalOneTimePrice: plan.addonDetailsList.reduce(
          (acc, addon) =>
            acc + addon.addonBillingType === AddonBillingType.OneTime
              ? addon.price
              : 0,
          0,
        ),
      });
    }
  }, [getPlanAgreementWithESignTokenQuery.data]);

  const onDocumentChange = (document: React.ReactElement) => {
    if (instance.loading) return;

    updateInstance(document);
  };

  const onActivateClick = async () => {
    setConfirmClicked(true);
    setIsLoading(true);
    const exportPromise = signatureRef.current?.exportImage();
    const signature = await exportPromise;
    if (signature) {
      setSignatureUrl(signature);
      setPdfKey(pdfKey + 1);
    }
    signatureRef.current?.clearCanvas();
    setHasDrawn(false);
  };

  useDebounceEffect(
    1000,
    () => {
      if (
        !finalizeReady ||
        !signatureUrl ||
        !instance.blob ||
        !getPlanAgreementWithESignTokenQuery.data?.eSignToken
      ) {
        return;
      }
      const file = new File([instance.blob], `purchase_summary.pdf`, {
        type: "application/pdf",
      });

      signAndActivatePlan({
        file: file,
        eSignToken: getPlanAgreementWithESignTokenQuery.data?.eSignToken,
      })
        .then(() => {
          setShowPurchaseComplete(true);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [
      finalizeReady,
      signatureUrl,
      instance.blob,
      getPlanAgreementWithESignTokenQuery.data?.eSignToken,
    ],
  );

  const finalizePlan = () => {
    setFinalizeReady(true);
  };

  const onSignAndCompleteClick = async () => {
    await onActivateClick();
    finalizePlan();
  };

  if (
    getPlanAgreementWithESignTokenQuery.isLoading ||
    !orderDetails ||
    !business
  ) {
    return <div>Loading...</div>;
  }

  if (showPurchaseComplete) {
    return (
      <EuiPanel
        hasBorder={false}
        hasShadow={false}
        style={{ overflow: "auto", padding: 12, minWidth: 600 }}
      >
        <h3>Purchase Complete</h3>
      </EuiPanel>
    );
  }

  return (
    <EuiPanel
      hasBorder={false}
      hasShadow={false}
      style={{ overflow: "auto", padding: 12, minWidth: 600 }}
    >
      <PurchaseLegalSignaturePdf
        orderDetails={orderDetails}
        business={business}
        pdfKey={pdfKey}
        signatureUrl={signatureUrl}
        onDocumentChange={onDocumentChange}
      />
      {!confirmClicked && (
        <EuiFormRow label={"Sign Here"} fullWidth>
          <ReactSketchCanvas
            width="100%"
            height="180px"
            canvasColor="transparent"
            strokeColor="#000000"
            ref={signatureRef}
            onStroke={() => setHasDrawn(true)}
          />
        </EuiFormRow>
      )}
      {getPlanAgreementWithESignTokenQuery.data?.hasPaymentMethod && (
        <>
          <EuiSpacer size="xxl" />
          <EuiFormRow>
            <EuiButton
              fill
              onClick={onSignAndCompleteClick}
              isLoading={isLoading || signAndActivatePlanQuery.isLoading}
              isDisabled={!hasDrawn}
            >
              Sign & Complete
            </EuiButton>
          </EuiFormRow>
        </>
      )}
      {!getPlanAgreementWithESignTokenQuery.data?.hasPaymentMethod && (
        <EuiFormRow>
          <StripeAddPaymentMethod
            return_url={window.location.href}
            businessId={business.id ?? ""}
            confirmText={"Sign & Complete"}
            onPaymentAdded={finalizePlan}
            prePaymentAddedAsync={onActivateClick}
            isLoading={isLoading || signAndActivatePlanQuery.isLoading}
            secret={getPlanAgreementWithESignTokenQuery.data?.clientSecret}
            isDisabled={!hasDrawn}
          />
        </EuiFormRow>
      )}
    </EuiPanel>
  );
};
